import type { Comparison } from "../../lib/comparisons";
import { partial, unstated, yes } from "./cells";

export const milirose: Comparison = {
  name: "Milirose",
  site: "milirose.com",
  linkLabel: "Alternative à Milirose",
  checkedOn: "2026-09-29",
  seoTitle: "Alternative à Milirose : Listou ou Milirose ? Comparatif 2026 · Listou",
  seoDescription:
    "Listou et Milirose sont gratuits et multi-boutiques. Listou se personnalise de A à Z, garde la surprise par défaut jusqu'après l'événement et se gère à deux. Le comparatif point par point.",
  h1: "Listou ou Milirose : quelle liste de naissance choisir ?",
  summary:
    "Listou et Milirose sont deux services gratuits, où l'on ajoute des articles de n'importe quelle boutique et où les proches réservent sans compte. La différence tient au design, au partage et à la surprise : une liste Listou se personnalise entièrement (modèle de page, palettes, police, stickers), se partage par QR code et visuels Instagram, garde la surprise par défaut jusqu'au lendemain de l'événement et se gère à deux. Milirose, qui annonce plus d'un million de listes de naissance publiées, propose une image d'en-tête et des couleurs de menu et de boutons, trois niveaux de surprise à choisir, et un champ RIB ou PayPal pour la cagnotte.",
  rows: [
    {
      feature: "Prix",
      listou: yes("Gratuit, sans limite de réservations"),
      them: yes("Gratuit, pour le créateur comme pour les proches"),
    },
    {
      feature: "Personnaliser le design de la liste",
      listou: yes("6 modèles de page, 13 palettes, 4 polices, 162 stickers, fonds à motifs, photo"),
      them: partial("Image d'en-tête, description, couleurs du menu et des boutons"),
    },
    {
      feature: "Compte pour offrir",
      listou: yes("Aucun : le lien suffit"),
      them: yes("Aucun compte pour réserver"),
    },
    {
      feature: "Compte pour créer la liste",
      listou: yes("Un email ou Google, sans mot de passe"),
      them: yes("Un compte avec mot de passe"),
    },
    {
      feature: "Articles de n'importe quelle boutique",
      listou: yes("Coller le lien : photo et prix récupérés"),
      them: yes("Oui : lien, bouton d'ajout rapide ou saisie à la main"),
    },
    {
      feature: "Visuels à partager",
      listou: yes("QR code pour le faire-part, visuels pour Instagram"),
      them: partial("Email, Facebook, WhatsApp, SMS ou Twitter"),
    },
    {
      feature: "Surprise activée d'emblée",
      listou: yes("Oui, par défaut, jusqu'au lendemain de l'événement"),
      them: partial("Réglage « Sans surprise » par défaut, à changer soi-même"),
    },
    {
      feature: "Surprise complète sur demande",
      listou: yes("Ni les réservations ni les noms, révélés le lendemain de l'événement"),
      them: yes("« Surprise totale » : ni les cadeaux réservés, ni les noms, ni la cagnotte"),
    },
    {
      feature: "Offrir sans que les parents sachent qui",
      listou: yes("Oui, « Rester anonyme », même après la révélation"),
      them: unstated("Pas d'option décrite dans la FAQ"),
    },
    {
      feature: "Gérer la liste à deux",
      listou: yes("Co-propriétaire, et invités qui peuvent modifier"),
      them: unstated("Pas de co-gestion décrite dans la FAQ"),
    },
    {
      feature: "Participation en argent",
      listou: partial("Lien vers votre cagnotte (Leetchi, Lydia…)"),
      them: yes("RIB/IBAN ou PayPal, sans commission Milirose"),
    },
    {
      feature: "Plusieurs exemplaires d'un article",
      listou: yes("Quantité, chacun en réserve une partie"),
      them: partial("Participations partielles, pour les cadeaux de plus de 25 €"),
    },
    {
      feature: "Secret Santa",
      listou: yes("Oui, avec exclusions, et des listes en groupe"),
      them: unstated("Non décrit ; une liste peut être classée en catégories"),
    },
    {
      feature: "Occasions",
      listou: yes("Naissance, Noël, anniversaire, mariage"),
      them: yes("Naissance, baptême, anniversaire, baby shower, Noël, mariage, crémaillère"),
    },
  ],
  body: `
## Une liste à votre image

Une liste de naissance, on l'envoie à toute la famille, on l'imprime sur le faire-part, on la montre sur Instagram. Elle mérite mieux qu'une page standard.

Sur **Listou**, tout se règle dans l'onglet Apparence, avec l'aperçu en direct :

- **6 modèles de page** : la mise en page elle-même change, pas seulement les couleurs.
- **13 palettes**, plus une couleur d'accent au choix.
- **4 polices** pour les titres : ronde, élégante, manuscrite ou éditoriale.
- **162 stickers** dessinés, en 18 thèmes (naissance, Noël, mariage…), pour décorer l'en-tête ou tapisser le fond en motifs.
- **Une photo de couverture** et un emoji pour la liste.

**Milirose** permet de personnaliser plusieurs éléments, d'après sa FAQ : l'image d'en-tête (des images sont proposées, ou vous téléchargez la vôtre en jpg, jpeg ou gif, jusqu'à 10 Mo), une description de l'événement, la couleur du texte du menu et de certains boutons, la couleur de fond du bandeau du menu et de certains boutons, et la couleur du logo du site. Vous pouvez aussi importer vos propres images pour les cadeaux. La FAQ ne mentionne ni choix de mise en page, ni polices, ni fonds.

## La surprise : deux approches

Les deux services savent cacher les cadeaux réservés. Ce qui change, c'est le réglage de départ et la façon de révéler.

Sur **Milirose**, le mode se choisit parmi trois niveaux, modifiables à tout moment :

- **Sans surprise**, le réglage par défaut : vous êtes notifié par email et dans la gestion de la liste à chaque réservation, avec le nom de la personne.
- **Demi-surprise** : plus de notification par email ; vous voyez quels cadeaux sont réservés, mais pas par qui, ni leur éventuel message.
- **Surprise totale** : vous ne voyez ni les cadeaux réservés, ni qui les a réservés, ni rien de la cagnotte.

Sur **Listou**, le mode surprise est activé par défaut. Tant qu'il est actif, ni vous ni votre co-parent ne voyez ce qui est réservé, ni par qui : vous recevez seulement « un cadeau vient d'être réservé ». La révélation a lieu le lendemain de la date de l'événement, ou quand vous cliquez sur « Révéler maintenant ». Si vous ouvrez le lien de votre propre liste, Listou vous montre l'aperçu que voient vos proches, sans les réservations.

Le niveau « Surprise totale » de Milirose est comparable à ce que fait Listou. Les différences précises : chez Listou la surprise est déjà active à la création, et la révélation est programmée à la date de l'événement. Sur Milirose, la FAQ ne décrit pas de moment de révélation : le niveau se change à la main. Pour une liste d'enfant que vous gérez vous-même, Listou se coupe en un clic.

## Offrir anonymement

Sur Milirose, la FAQ indique que le mail de réservation envoyé au créateur donne le cadeau, la personne qui l'offre et son message. Aucune option permettant à la personne qui offre de rester anonyme n'y est décrite.

Sur Listou, cocher « Rester anonyme » cache le nom à la personne qui reçoit, y compris après la révélation : elle voit « un proche ». Les autres invités ne voient jamais le prénom, seulement que le cadeau est pris.

## Préparer la liste à deux

Une liste de naissance se fait rarement seul. Sur Listou, vous invitez le co-parent comme **co-propriétaire** : il a les mêmes droits que vous, et la surprise reste gardée pour vous deux. Vous pouvez aussi inviter la marraine ou une amie à « modifier » : elle aide à remplir la liste sans toucher au partage ni à la surprise.

La FAQ de Milirose ne décrit pas de co-gestion : la liste s'administre depuis le compte de son créateur, dans l'onglet « Gérer ».

## Ajouter des articles

Les deux services acceptent n'importe quelle boutique. On peut aussi ajouter une envie à la main, pour un cadeau fait main ou repéré en magasin.

Sur **Listou**, on colle le lien du produit : le nom, la photo et le prix sont récupérés. L'assistant d'idées propose des articles selon l'occasion, et on peut piocher parmi ceux que d'autres parents ont déjà ajoutés à leurs listes. Trois niveaux d'envie, « Coup de cœur », « Envie » et « Idée », disent à vos proches ce qui compte le plus.

Sur **Milirose**, on peut coller l'URL du produit dans le formulaire « Ajouter un cadeau » (la fiche se remplit en partie ou en totalité), utiliser le bouton d'ajout rapide glissé dans la barre de favoris sur ordinateur, ou remplir la fiche à la main. Le site propose aussi un assistant de naissance, une sélection de sites marchands et les cadeaux les plus ajoutés. Un cœur marque les envies prioritaires, on peut réordonner les cadeaux sur ordinateur, et on peut créer ses propres catégories.

## Partager la liste

Sur **Milirose**, la FAQ décrit cinq façons de diffuser la liste : un email pré-rédigé avec l'image d'en-tête et le lien, un partage Facebook, WhatsApp, SMS et Twitter. Elle mentionne aussi que les listes privées se communiquent par leur lien.

Sur **Listou**, l'onglet Partage donne le lien, un **QR code** à imprimer sur le faire-part, et des **visuels prêts pour Instagram**. Le lien a été trop partagé ? Vous en générez un nouveau, et l'ancien cesse de fonctionner.

## Ce que vivent vos proches

C'est souvent la grand-mère qui décide si une liste « marche ». Sur les deux services, elle ouvre le lien et réserve sans créer de compte. Sur Milirose, elle clique sur « Offrir » puis est redirigée vers le site marchand pour acheter. Sur Listou, le prénom et l'email sont facultatifs, et le bouton « Voir sur… » mène à la boutique choisie, sans obligation de l'utiliser.

Sur Listou, un cadeau réservé apparaît aussitôt comme pris pour les autres invités. Pour un article voulu en plusieurs exemplaires (trois bodies, deux assiettes), chacun en réserve une partie et la liste indique combien il en reste.

Une différence sur l'adresse de livraison : d'après la FAQ de Milirose, elle est visible par toutes les personnes qui consultent la liste, et reprise dans l'email de confirmation. Sur Listou, elle n'est jamais affichée sur la liste : un proche la voit seulement après avoir réservé.

## Les participations en argent

Aucun des deux ne fait transiter d'argent.

**Milirose** propose un champ « Cagnotte » où l'on renseigne son RIB/IBAN ou son compte PayPal. Les proches y participent par virement ou PayPal, sur le compte du créateur. Milirose ne prélève aucune commission, ni sur la cagnotte, ni sur les participations partielles. Ces participations partielles se règlent cadeau par cadeau, et seulement pour les articles de plus de 25 €.

**Listou** propose d'ajouter un lien vers votre cagnotte externe (Leetchi, Lydia…). Un bloc « Participer librement » apparaît en bas de la liste. Il n'y a pas de champ pour un IBAN. Si vous voulez recevoir des virements directs affichés sur la liste, Milirose est plus direct.

## Noël et les fêtes à plusieurs

Sur **Listou**, un **groupe** réunit plusieurs listes derrière un seul lien, « Noël chez les Martin ». Chacun choisit son prénom, remplit sa liste et réserve sur celles des autres. On peut y ajouter un **Secret Santa** avec exclusions : un couple ne se tire pas lui-même.

**Milirose** propose des listes de Noël, et sa FAQ suggère de créer des catégories par prénom pour n'avoir qu'une seule liste pour toute la famille à Noël. Elle ne décrit pas de tirage au sort de Secret Santa.

## Passer de Milirose à Listou

Il n'y a pas d'import automatique. La migration prend quelques minutes :

1. Créez votre liste sur Listou, avec la date de l'événement : la surprise est déjà activée.
2. Pour chaque article, copiez le lien de la boutique depuis votre liste Milirose et collez-le dans Listou. Le nom, la photo et le prix sont récupérés.
3. Invitez votre co-parent dans les réglages, et choisissez modèle, palette et stickers dans l'onglet Apparence.
4. Envoyez le nouveau lien à vos proches, ou imprimez son QR code sur le faire-part.

Si des cadeaux ont déjà été réservés sur Milirose, retirez-les de la nouvelle liste pour éviter les doublons. Si vous utilisez la cagnotte, ajoutez le lien de votre cagnotte externe.
`,
  chooseThem: [
    "Vous voulez afficher votre RIB/IBAN ou votre compte PayPal aux proches, sans passer par un service de cagnotte.",
    "Vous voulez des occasions comme le baptême, le baby shower ou la crémaillère, proposées comme telles.",
    "Vous appréciez de choisir vous-même parmi trois niveaux de surprise, ou de suivre des statistiques de liste.",
  ],
  chooseListou: [
    "Vous voulez une liste qui vous ressemble : modèle, couleurs, police et stickers.",
    "Vous voulez que la surprise soit gardée d'emblée, et révélée le lendemain de l'événement.",
    "Vous préparez la liste à deux, avec un co-propriétaire.",
    "Vos proches offrent parfois anonymement, et ce choix doit être respecté jusqu'au bout.",
    "Vous organisez aussi Noël en famille : listes en groupe et Secret Santa au même endroit.",
  ],
  faq: [
    [
      "Listou est-il une bonne alternative à Milirose ?",
      "Oui, surtout si vous tenez au design de votre liste, au partage ou à la surprise gardée par défaut. Les deux sont gratuits, acceptent n'importe quelle boutique et ne demandent pas de compte aux proches. Listou se personnalise entièrement, se partage par QR code et permet un co-propriétaire ; Milirose peut afficher votre RIB ou PayPal aux participants à la cagnotte.",
    ],
    [
      "Peut-on personnaliser le design de sa liste ?",
      "Sur Listou, oui, entièrement : 6 modèles de page, 13 palettes et une couleur d'accent, 4 polices de titre, 162 stickers en 18 thèmes, des fonds à motifs et une photo de couverture. Sur Milirose, on choisit une image d'en-tête, une description, et les couleurs du menu, de certains boutons et du logo.",
    ],
    [
      "La surprise est-elle activée par défaut ?",
      "Sur Listou, oui : le mode surprise est actif dès la création, et les réservations sont révélées le lendemain de l'événement ou à la demande. Sur Milirose, le réglage par défaut est « Sans surprise » ; la « Surprise totale » se choisit dans les options et se change à tout moment.",
    ],
    [
      "Peut-on offrir anonymement ?",
      "Sur Listou, oui : « Rester anonyme » cache le nom à la personne qui reçoit, même après la révélation. La FAQ de Milirose ne décrit pas d'option équivalente.",
    ],
    [
      "Peut-on gérer une liste à deux ?",
      "Sur Listou, le co-parent devient co-propriétaire, avec les mêmes droits, et une amie peut être invitée à modifier. La FAQ de Milirose ne décrit pas de co-gestion.",
    ],
    [
      "Faut-il un compte pour offrir un cadeau ?",
      "Non, ni sur Listou ni sur Milirose. Les proches ouvrent le lien et réservent. Sur Listou, le prénom et l'email sont facultatifs.",
    ],
    [
      "Peut-on recevoir de l'argent plutôt que des cadeaux ?",
      "Sur Milirose, vous pouvez afficher votre RIB/IBAN ou votre compte PayPal, et l'argent ne transite pas par Milirose. Sur Listou, vous ajoutez un lien vers votre cagnotte (Leetchi, Lydia, PayPal…). Aucun des deux ne fait transiter d'argent.",
    ],
    [
      "Peut-on passer de Milirose à Listou ?",
      "Il n'y a pas d'import automatique. Recréez la liste sur Listou en collant les liens de vos articles : le nom, la photo et le prix sont récupérés à chaque fois. Listou et Milirose sont gratuits ; Listou se rémunère par une commission que certaines boutiques versent quand un proche achète via la liste, sans changer le prix.",
    ],
  ],
  sources: [
    { label: "Milirose : page d'accueil liste de naissance", url: "https://www.milirose.com/" },
    { label: "Milirose : foire aux questions", url: "https://www.milirose.com/faq.html" },
  ],
};
